import { useState } from 'react'
import { Button, ButtonGroup, P, RadioGroup, Wrapper } from '@farewill/ui'

import store from 'state/create-store'
import { hideModal } from 'state/actions'
import { Address } from 'lib/models/address'
import { NominatedRepresentative } from 'lib/models/lead'
import MessageBox from 'components/message-box'

type PrintableAddress =
  Address | NominatedRepresentative['addressFields'] | undefined

export type DocumentRecipientType =
  'purchaser' | 'nominatedRepresentative' | 'both'

const formatAddress = (address: PrintableAddress): string | undefined => {
  if (!address?.postalCode) return undefined

  return `${address.lineOne} ${address.lineTwo}, ${address.city}, ${address.postalCode} ${address.countryCode}`
}

const RecipientAddress = ({
  name,
  address,
}: {
  name: string
  address: string
}) => (
  <>
    <P>
      Documents for <strong>{name}</strong> will be sent to:
    </P>
    <P>{address}</P>
  </>
)

type GenerateDocumentsModalConfig = {
  config: {
    purchaserName: string
    purchaserAddress: Address | undefined
    nominatedRepresentativeName?: string
    nominatedRepresentativeAddress?: NominatedRepresentative['addressFields']
    sendNominatedRepDocumentsSeparately: boolean
    confirmCallback: (recipientType: DocumentRecipientType) => Promise<void>
  }
}

const GenerateDocumentsModal = ({ config }: GenerateDocumentsModalConfig) => {
  const [recipientType, setRecipientType] =
    useState<DocumentRecipientType>('both')

  const formattedPurchaserAddress = formatAddress(config.purchaserAddress)
  const formattedNominatedRepresentativeAddress = formatAddress(
    config.nominatedRepresentativeAddress
  )
  const nominatedRepresentativeName = config.nominatedRepresentativeName
  const hasPurchaserAddress = !!formattedPurchaserAddress
  const showRecipientSelector =
    config.sendNominatedRepDocumentsSeparately &&
    !!nominatedRepresentativeName &&
    !!formattedNominatedRepresentativeAddress

  const handleContinueClick = async () => {
    await config.confirmCallback(recipientType)
    store.dispatch(hideModal())
  }

  return (
    <Wrapper>
      {hasPurchaserAddress ? (
        <>
          <P>
            This will post a new set of conversion pack documents to PurePrint
            for printing and posting.
          </P>
          {showRecipientSelector &&
          nominatedRepresentativeName &&
          formattedNominatedRepresentativeAddress ? (
            <>
              <RadioGroup
                name="recipientType"
                legend="Select which documents to send:"
                value={recipientType}
                onChange={(event) =>
                  setRecipientType(event.target.value as DocumentRecipientType)
                }
                options={[
                  {
                    value: 'both',
                    label:
                      'Both purchaser and nominated representative documents',
                  },
                  {
                    value: 'purchaser',
                    label: 'Purchaser documents only',
                  },
                  {
                    value: 'nominatedRepresentative',
                    label: 'Nominated representative documents only',
                  },
                ]}
              />
              {recipientType === 'both' && (
                <>
                  <RecipientAddress
                    name={config.purchaserName}
                    address={formattedPurchaserAddress}
                  />
                  <RecipientAddress
                    name={nominatedRepresentativeName}
                    address={formattedNominatedRepresentativeAddress}
                  />
                </>
              )}
              {recipientType === 'purchaser' && (
                <RecipientAddress
                  name={config.purchaserName}
                  address={formattedPurchaserAddress}
                />
              )}
              {recipientType === 'nominatedRepresentative' && (
                <RecipientAddress
                  name={nominatedRepresentativeName}
                  address={formattedNominatedRepresentativeAddress}
                />
              )}
            </>
          ) : (
            <>
              <P>
                Documents for <strong>{config.purchaserName}</strong> will be
                sent to:
              </P>
              <P>{formattedPurchaserAddress}</P>
            </>
          )}
          <MessageBox margin={[0, 0, 'M']}>
            <P>
              Please check that all plan details are correct before confirming.
            </P>
            <P>
              Any existing print orders for this recipient that have not yet
              been dispatched will be cancelled automatically.
            </P>
          </MessageBox>

          <ButtonGroup horizontal gap="S">
            <Button.Primary type="button" onClick={handleContinueClick}>
              Generate and send documents
            </Button.Primary>
            <Button.Bordered
              type="button"
              onClick={() => store.dispatch(hideModal())}
            >
              Cancel
            </Button.Bordered>
          </ButtonGroup>
        </>
      ) : (
        <>
          <P>
            We need a purchaser address to send the conversion pack documents.
          </P>
          <P margin={[0, 0, 'M']}>
            Go back to the plan details and add an address before you try to
            post documents.
          </P>
          <Button.Primary
            type="button"
            onClick={() => store.dispatch(hideModal())}
          >
            Go back
          </Button.Primary>
        </>
      )}
    </Wrapper>
  )
}

export default GenerateDocumentsModal
